import { useCallback, useEffect, useRef, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import EmptyState from '../components/EmptyState'
import PanelExpediente, { BarrasDeDocumentos, LineaDeCierre } from '../components/expediente/PanelExpediente'
import { EXPEDIENTE_LISTO } from '../eventos'
import {
  PESTANAS_MIS_EXPEDIENTES, agruparPorDia, diaDeLima, fechaDeLaPestana, horaLima,
  parametrosDeMisExpedientes,
  type Descarga, type FilaExpediente, type MisExpedientes as RespuestaMisExpedientes,
  type PestanaMisExpedientes,
} from '../expedientes'

/**
 * «Mis expedientes» — los expedientes del asesor, de la entrega a la descarga
 * (03-nov-2026).
 *
 * Pedido de Leslie: los que el agente completó solo los ve el asesor asignado,
 * sabiendo que se completaron, y él los descarga. Antes los veía con el chip
 * «Completo por IA» solo 24 h en Mi bandeja, sin aviso. Acá la lista no vence:
 * un expediente queda en «Por descargar» hasta que alguien lo baje.
 *
 * Del más reciente al más antiguo, agrupado por día, con filtro de fechas y sin
 * botones en las filas: tocar una abre el panel del expediente, con los
 * documentos y el chat — el mismo panel de Operación › Expedientes.
 */

/** Espera antes de buscar mientras se escribe. */
const ESPERA_BUSQUEDA_MS = 400
/** Cada cuánto se vuelve a pedir, además del aviso del Layout. */
const REFRESCO_MS = 60_000

export default function MisExpedientes() {
  const [pestana, setPestana] = useState<PestanaMisExpedientes>('por_descargar')
  const [desde, setDesde] = useState(() => diaDeLima(Date.now(), 30))
  const [hasta, setHasta] = useState(() => diaDeLima(Date.now()))
  const [texto, setTexto] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [datos, setDatos] = useState<RespuestaMisExpedientes | null>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [seleccion, setSeleccion] = useState<FilaExpediente | null>(null)
  const pedido = useRef(0)

  useEffect(() => {
    const t = setTimeout(() => setBusqueda(texto), ESPERA_BUSQUEDA_MS)
    return () => clearTimeout(t)
  }, [texto])

  const cargar = useCallback(async (silencioso = false) => {
    const n = ++pedido.current
    if (!silencioso) setCargando(true)
    try {
      const res = await apiClient.get<RespuestaMisExpedientes>('/mis-expedientes', {
        params: parametrosDeMisExpedientes(pestana, desde, hasta, busqueda),
      })
      if (n !== pedido.current) return
      setDatos(res.data)
      setError(null)
      // La seleccionada se queda abierta con lo nuevo; si cambió de pestaña,
      // el panel la sigue mostrando hasta que se elija otra.
      setSeleccion((s) => res.data.filas.find((f) => f.conversation_id === s?.conversation_id)
        ?? s ?? res.data.filas[0] ?? null)
    } catch (err) {
      if (n === pedido.current) setError(errorMessage(err, 'No se pudieron cargar tus expedientes.'))
    } finally {
      if (n === pedido.current) setCargando(false)
    }
  }, [pestana, desde, hasta, busqueda])

  useEffect(() => { void cargar() }, [cargar])
  useEffect(() => {
    const refrescar = () => { void cargar(true) }
    window.addEventListener(EXPEDIENTE_LISTO, refrescar)
    const t = setInterval(refrescar, REFRESCO_MS)
    return () => {
      window.removeEventListener(EXPEDIENTE_LISTO, refrescar)
      clearInterval(t)
    }
  }, [cargar])

  const alDescargar = useCallback((descarga: Descarga) => {
    setSeleccion((s) => (s ? { ...s, descarga, por_descargar: false } : s))
    void cargar(true)
  }, [cargar])

  const filas = datos?.filas ?? []
  const grupos = agruparPorDia(filas, (f) => fechaDeLaPestana(f, pestana), Date.now())

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5">
        <div role="group" aria-label="Paso" className="flex flex-wrap gap-1.5">
          {PESTANAS_MIS_EXPEDIENTES.map((p) => (
            <button key={p.valor} type="button" aria-pressed={pestana === p.valor}
                    className={pestana === p.valor ? 'pill-filter-active' : 'pill-filter'}
                    onClick={() => { setPestana(p.valor); setSeleccion(null) }}>
              {p.label}{datos ? ` ${datos.conteos[p.valor]}` : ''}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2">
            <span className="text-body font-bold text-ink-muted">Desde</span>
            <input type="date" className="input" value={desde} max={hasta || undefined}
                   onChange={(e) => setDesde(e.target.value)} />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-body font-bold text-ink-muted">Hasta</span>
            <input type="date" className="input" value={hasta} min={desde || undefined}
                   onChange={(e) => setHasta(e.target.value)} />
          </label>
          <label className="flex items-center gap-2">
            <span className="sr-only">Buscar</span>
            <input type="search" className="input w-[200px]" placeholder="Buscar cliente o DNI"
                   value={texto} onChange={(e) => setTexto(e.target.value)} />
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <section aria-label={PESTANAS_MIS_EXPEDIENTES.find((p) => p.valor === pestana)?.label}
                 className="card min-w-0 flex-[999_1_460px] overflow-hidden">
          {error ? (
            <div className="flex flex-col items-center gap-3 px-5 py-10">
              <p className="text-body text-danger">{error}</p>
              <button className="btn-ghost" onClick={() => void cargar()}>Reintentar</button>
            </div>
          ) : cargando && !datos ? (
            <p className="px-5 py-10 text-center text-body text-ink-muted">Cargando tus expedientes…</p>
          ) : filas.length === 0 ? (
            <div className="px-5 py-10">
              <EmptyState
                title={pestana === 'por_descargar' ? 'Nada por descargar' : 'Sin expedientes'}
                description={pestana === 'por_descargar'
                  ? 'Cuando un cliente tuyo entregue todo, aparece acá.'
                  : 'No hay expedientes tuyos en esta pestaña con estas fechas.'} />
            </div>
          ) : grupos.map((g) => (
            <div key={`${g.dia}-${g.filas[0].conversation_id}`}>
              <h2 className="border-b border-surface-faint bg-surface-alt px-5 py-2 text-micro font-extrabold uppercase tracking-wide text-ink-muted">
                {g.titulo} · {g.filas.length}
              </h2>
              <ul>
                {g.filas.map((f) => {
                  const abierta = seleccion?.conversation_id === f.conversation_id
                  return (
                    <li key={f.conversation_id}>
                      <button type="button" aria-current={abierta || undefined}
                              onClick={() => setSeleccion(f)}
                              className={`flex w-full items-center gap-3.5 border-b border-l-[3px] border-b-surface-faint px-5 py-3 text-left transition-colors ${abierta
                                ? 'border-l-brand bg-brand-selected' : 'border-l-transparent hover:bg-surface'}`}>
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-baseline gap-2">
                            <span className="text-chat font-extrabold text-ink">{f.nombre || 'Sin identificar'}</span>
                            {f.dni && <span className="font-mono text-meta text-ink-faint">DNI {f.dni}</span>}
                          </span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                            {f.como_cerro ? <LineaDeCierre fila={f} />
                              : <span className="text-micro font-bold text-ink-muted">{f.fase_label}</span>}
                            <span className="text-micro text-ink-faint">
                              · {pestana === 'en_curso' ? `entró ${horaLima(f.entrada)}` : `listo ${horaLima(f.completo_ts)}`}
                            </span>
                          </span>
                        </span>
                        <BarrasDeDocumentos fila={f} />
                        <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
                             className={abierta ? 'text-brand' : 'text-ink-ghost'}>
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </section>

        {seleccion ? (
          <div className="sticky top-4 flex max-h-[calc(100vh-120px)] min-w-0 flex-[1_1_360px] flex-col">
            <PanelExpediente
              fila={seleccion}
              modo="asesor"
              onCerrar={() => setSeleccion(null)}
              onDescargado={alDescargar}
            />
          </div>
        ) : filas.length > 0 && (
          <p className="flex-[1_1_360px] self-center text-center text-body text-ink-muted">
            Elige un expediente para ver sus documentos y su chat.
          </p>
        )}
      </div>
    </div>
  )
}
